<template>
  <div>
    <!-- Trigger Button (Create Mode) -->
    <v-btn 
      v-if="item && item.id < 0" 
      color="accent" 
      class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 h-44 shadow-sm"
      @click="openDialog"
      :disabled="!$can('create','Activity')"
    >
      <v-icon left size="14">fas fa-plus</v-icon>
      Nueva Actividad
    </v-btn>

    <!-- Trigger Icon (Update Mode) -->
    <v-btn
      v-else
      icon
      small
      color="primary"
      class="grey lighten-4 shadow-sm rounded-lg"
      @click="openDialog"
      :disabled="!$can('update','Activity')"
    >
      <v-icon size="12">fas fa-pencil-alt</v-icon>
    </v-btn>

    <!-- Main Creation/Update Dialog -->
    <v-dialog 
      v-model="showDialog" 
      max-width="500" 
      persistent
      overlay-color="primary"
      overlay-opacity="0.2"
    >
      <v-card class="modern-card overflow-hidden">
        <!-- Premium Header Pattern -->
        <v-sheet color="grey lighten-4" class="px-8 py-4 border-bottom d-flex align-center">
          <v-avatar color="primary" size="32" class="mr-3 shadow-sm border-white-sm">
            <v-icon color="white" x-small>fas fa-calendar-plus</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-black primary--text lh-1">
              {{ item && item.id > -1 ? 'Editar Actividad' : 'Nueva Gestión' }}
            </div>
            <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Registro de interacción comercial</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon small @click="cancel" class="grey lighten-3 shadow-sm">
            <v-icon x-small>fas fa-times</v-icon>
          </v-btn>
        </v-sheet>

        <v-card-text class="pa-8">
          <ValidationObserver v-slot="{ handleSubmit }" ref="observer">
            <v-form @submit.prevent="handleSubmit(save)">
              
              <v-row dense>
                <v-col cols="12">
                  <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Fecha de Gestión</div>
                  <ValidationProvider name="Fecha" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='date'
                      type="date"
                      dense
                      outlined
                      class="modern-input"
                      :error-messages="errors"
                      :loading="loading"
                      hide-details="auto"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Tipo de Actividad</div>
                  <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
                    <tipo-activity-selection
                      v-model='tipo'
                      :errors="errors"
                      :loading="loading"
                      :filtro="false"
                      class="mt-0"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Nombre del Cliente / Empresa</div>
                  <ValidationProvider name="Cliente" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='customer_name'
                      placeholder="Ingrese razón social o nombre fantasía"
                      dense
                      outlined
                      class="modern-input"
                      :error-messages="errors"
                      :loading="loading"
                      hide-details="auto"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Contacto Atendido</div>
                  <ValidationProvider name="Contacto" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='contact'
                      placeholder="¿Quién nos recibió o atendió?"
                      dense
                      outlined
                      class="modern-input"
                      :error-messages="errors"
                      :loading="loading"
                      hide-details="auto"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Observaciones de la Gestión</div>
                  <v-textarea
                    v-model='notes'
                    placeholder="Resumen de lo conversado, tareas pendientes o acuerdos..."
                    rows="4"
                    dense
                    outlined
                    class="modern-input"
                    :loading="loading"
                    hide-details="auto"
                  ></v-textarea>
                </v-col>
              </v-row>

              <!-- Action Buttons Section -->
              <div class="d-flex align-center justify-space-between mt-10">
                <v-btn 
                  v-if="item && item.id > -1" 
                  text
                  color="error"
                  class="font-weight-black text-uppercase tracking-widest rounded-lg px-0"
                  @click="showConfirmDelete = true" 
                  :disabled="loading"
                >
                  <v-icon left size="12">fas fa-trash-alt</v-icon>
                  Eliminar
                </v-btn>
                <div v-else></div>

                <div class="d-flex">
                  <v-btn 
                    text 
                    @click="cancel" 
                    class="mr-2 font-weight-bold text-uppercase tracking-widest rounded-lg grey--text" 
                    :disabled="loading"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn 
                    type="submit" 
                    color="primary" 
                    class="font-weight-black text-uppercase tracking-widest rounded-lg px-8 h-44 shadow-sm"
                    :loading="loading"
                  >
                    <v-icon left size="14">fas fa-save</v-icon>
                    Guardar
                  </v-btn>
                </div>
              </div>
            </v-form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Modal -->
    <confirm
      v-if="showConfirmDelete"
      v-model="showConfirmDelete" 
      :message="`¿Desea eliminar este registro de actividad de forma permanente?`"
      @ok="deleteItem"
      @cancel="showConfirmDelete = false"
    />
  </div>
</template>

<script>
import { updateActivity, deleteActivity, createActivity } from '@/api/salesNotes'
import notifications from '@/utils/notifications'
import TipoActivitySelection from '@/components/sales/TipoActivitySelection'
import { ValidationObserver, ValidationProvider } from 'vee-validate'

export default {
  name: 'ActivityCreateUpdate',
  components: {
    TipoActivitySelection,
    ValidationObserver,
    ValidationProvider,
    Confirm: () => import('@/components/alerts/Confirm'),
  },
  props: {
    id: {
      type: Number,
      default: -1
    },
    item: {
      type: Object,
      default: () => ({ id: -1 })
    }
  },
  data() {
    return {
      loading: false,
      showConfirmDelete: false,
      showDialog: false,
      date: '',
      tipo: '',
      contact: '',
      customer_name: '',
      notes: '',
    }
  },
  methods: {
    openDialog() {
      if (this.item && this.item.id > -1) {
        this.date = this.item.date ? this.item.date.split('T')[0] : ''
        this.tipo = typeof this.item.tipo === 'string' ? this.item.tipo : this.item.tipo
        this.contact = this.item.contact
        this.notes = this.item.notes
        this.customer_name = this.item.customer_name
      } else {
        this.resetForm()
        this.date = new Date().toISOString().split('T')[0]
      }
      this.showDialog = true
    },
    resetForm() {
      this.date = ''
      this.tipo = ''
      this.contact = ''
      this.notes = ''
      this.customer_name = ''
      if (this.$refs.observer) {
        this.$refs.observer.reset()
      }
    },
    async save() {
      this.loading = true
      const data = {
        date: this.date,
        tipo: this.tipo,
        contact: this.contact,
        notes: this.notes,
        customer_name: this.customer_name
      }
      
      try {
        if (this.item && this.item.id > -1) {
          await updateActivity(this.item.id, data)
          notifications.showSuccess('Gestión comercial actualizada correctamente')
        } else {
          await createActivity(data)
          notifications.showSuccess('Gestión comercial registrada con éxito')
        }
        this.$emit('updated')
        this.showDialog = false
        this.resetForm()
      } catch (err) {
        notifications.showError('Error al guardar la actividad', err)
      } finally {
        this.loading = false
      }
    },
    async deleteItem() {
      this.loading = true
      try {
        await deleteActivity(this.item.id)
        notifications.showSuccess('Registro eliminado correctamente')
        this.showConfirmDelete = false
        this.showDialog = false
        this.$emit('updated')
      } catch (err) {
        notifications.showError('Error al eliminar', err)
      } finally {
        this.loading = false
      }
    },
    cancel() {
      this.showDialog = false
      this.resetForm()
    }
  }
}
</script>

<style lang="scss" scoped>
.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; background: white; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.03) !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.border-white-sm { border: 2px solid white !important; }
.tracking-widest { letter-spacing: 1px !important; }
.lh-1 { line-height: 1.2; }
.h-44 { height: 44px !important; }

.modern-input {
  ::v-deep .v-input__control {
    .v-input__slot {
      border-radius: 8px !important;
      background-color: #F8FAFC !important;
      border: 1px solid #E2E8F0 !important;
      box-shadow: none !important;
      
      fieldset { border: none; }
    }
  }
}

::v-deep .v-dialog {
  border-radius: 12px !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}
</style>